import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff, User, Shield } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Switch,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@b2b/ui-components";

import {
  SubAccountFormData,
  SubAccount,
  AVAILABLE_PERMISSIONS,
  SUB_ACCOUNT_ROLES,
} from "../types/subAccount";
import { cn } from "../../../lib/utils";

interface SubAccountFormProps {
  initialData?: SubAccount | null;
  onSubmit: (data: SubAccountFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const SubAccountForm = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}: SubAccountFormProps) => {
  const [formData, setFormData] = useState<SubAccountFormData>({
    username: "",
    email: "",
    mobile: "",
    password: "",
    role: "",
    permissions: [],
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditMode = !!initialData;

  useEffect(() => {
    if (initialData) {
      setFormData({
        username: initialData.username,
        email: initialData.email,
        mobile: initialData.mobile,
        password: "", // Don't prefill password in edit mode
        role: initialData.role,
        permissions: initialData.permissions,
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    } else if (formData.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    }

    if (!isEditMode && !formData.password) {
      newErrors.password = "Password is required";
    } else if (!isEditMode && formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (!formData.role) {
      newErrors.role = "Role is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRoleChange = (roleId: string) => {
    const role = SUB_ACCOUNT_ROLES.find((r) => r.id === roleId);
    if (role) {
      setFormData((prev) => ({
        ...prev,
        role: roleId,
        permissions: role.defaultPermissions,
      }));
    }
  };

  const handlePermissionToggle = (permissionId: string) => {
    // Admin role should have all permissions and they shouldn't be changeable
    if (formData.role === "admin") return;

    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter((p) => p !== permissionId)
        : [...prev.permissions, permissionId],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const selectedRole = SUB_ACCOUNT_ROLES.find((r) => r.id === formData.role);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
          <User className="h-5 w-5 text-orange-600" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">
            {isEditMode ? "Edit Sub-Account" : "Add New Sub-Account"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isEditMode
              ? "Update user information and permissions"
              : "Create a new team member account"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="username">
                  Username <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      username: e.target.value,
                    }))
                  }
                  className={cn(errors.username && "border-destructive")}
                  placeholder="Enter username"
                />
                {errors.username && (
                  <p className="text-sm text-destructive">{errors.username}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">
                  Email Address <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                  }
                  className={cn(errors.email && "border-destructive")}
                  placeholder="Enter email address"
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile">
                  Mobile Number <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="mobile"
                  value={formData.mobile}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, mobile: e.target.value }))
                  }
                  className={cn(errors.mobile && "border-destructive")}
                  placeholder="Enter mobile number"
                />
                {errors.mobile && (
                  <p className="text-sm text-destructive">{errors.mobile}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  {isEditMode ? "New Password (optional)" : "Password"}
                  {!isEditMode && <span className="text-destructive">*</span>}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    className={cn(
                      "pr-10",
                      errors.password && "border-destructive"
                    )}
                    placeholder={
                      isEditMode
                        ? "Leave blank to keep current"
                        : "Enter password"
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Role Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Role & Permissions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="role">
                Role <span className="text-destructive">*</span>
              </Label>
              <Select value={formData.role} onValueChange={handleRoleChange}>
                <SelectTrigger
                  className={cn("w-full", errors.role && "border-destructive")}
                >
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {SUB_ACCOUNT_ROLES.map((role) => (
                    <SelectItem
                      key={role.id}
                      value={role.id}
                      className="hover:bg-orange-100 hover:text-orange-500"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{role.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {role.defaultPermissions.length} permissions
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.role && (
                <p className="text-sm text-destructive">{errors.role}</p>
              )}
            </div>

            {selectedRole && (
              <div className="space-y-4">
                <Separator />
                <div>
                  <h4 className="text-sm font-medium mb-3">Permissions</h4>
                  {formData.role === "admin" && (
                    <p className="text-xs text-muted-foreground mb-4 p-3 bg-primary/5 rounded-lg border-l-2 border-primary">
                      Admin users have full access to all features. Permissions
                      cannot be modified.
                    </p>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {AVAILABLE_PERMISSIONS.map((permission) => {
                      const isEnabled = formData.permissions.includes(
                        permission.id
                      );
                      const isDisabled = formData.role === "admin";

                      return (
                        <div
                          key={permission.id}
                          className={cn(
                            "flex items-center justify-between p-3 border rounded-lg transition-colors",
                            isEnabled
                              ? "bg-primary/5 border-primary/20"
                              : "bg-muted/20 border-border",
                            isDisabled && "opacity-75"
                          )}
                        >
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={() =>
                                  handlePermissionToggle(permission.id)
                                }
                                disabled={isDisabled}
                              />
                              <div>
                                <p className="text-sm font-medium">
                                  {permission.label}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {permission.description}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Form Actions */}
        <div className="flex items-center gap-3 pt-4">
          <Button
            type="submit"
            variant="success"
            disabled={isLoading}
            className="min-w-32"
          >
            {isLoading
              ? "Saving..."
              : isEditMode
              ? "Save Changes"
              : "Create Account"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </motion.div>
  );
};
